import React, { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  Loader2, ChevronRight, ChevronLeft, CheckCircle, MessageCircle,
  User, Tag, Percent, Shield, Eye, EyeOff, Search
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VEHICLE_TYPE_OPTIONS = [
  { key: 'car', label: 'Car', emoji: '🚗' },
  { key: 'bike', label: 'Bike', emoji: '🏍️' },
  { key: 'ev', label: 'Electric', emoji: '⚡' },
  { key: 'commercial', label: 'Commercial', emoji: '🚛' },
];

const defaultForm = {
  first_name: '',
  last_name: '',
  phone: '',
  email: '',
  city: '',
  coupon_code: '',
  vehicle_types: [],
  discount_percentage: '',
  payout_cycle_days: 30,
  password: '',
};

export default function InfluencerCreateDialog({ open, onClose, cities, token, onCreated }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(defaultForm);
  const [loading, setLoading] = useState(false);
  const [created, setCreated] = useState(null);   // holds response after creation
  const [showPass, setShowPass] = useState(false);
  const [citySearch, setCitySearch] = useState('');

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const filteredCities = cities.filter(c =>
    !citySearch || c.name?.toLowerCase().includes(citySearch.toLowerCase())
  );

  const set = (field, val) => setForm(f => ({ ...f, [field]: val }));

  const toggleVehicleType = (key) => {
    setForm(f => ({
      ...f,
      vehicle_types: f.vehicle_types.includes(key)
        ? f.vehicle_types.filter(v => v !== key)
        : [...f.vehicle_types, key],
    }));
  };

  const validateStep1 = () => {
    if (!form.first_name.trim()) { toast.error('First name is required'); return false; }
    if (!form.last_name.trim()) { toast.error('Last name is required'); return false; }
    if (!form.phone || form.phone.replace(/\D/g, '').length !== 10) {
      toast.error('Valid 10-digit phone is required'); return false;
    }
    if (!form.email || !/\S+@\S+\.\S+/.test(form.email)) {
      toast.error('Valid email is required'); return false;
    }
    if (!form.city) { toast.error('City is required'); return false; }
    return true;
  };

  const validateStep2 = () => {
    const code = (form.coupon_code || '').toUpperCase().trim();
    if (code.length < 6 || code.length > 8) {
      toast.error('Coupon code must be 6–8 characters'); return false;
    }
    if (!/^[A-Z0-9]+$/.test(code)) {
      toast.error('Coupon code must be alphanumeric only'); return false;
    }
    if (!form.vehicle_types.length) {
      toast.error('Select at least one vehicle type'); return false;
    }
    const disc = parseFloat(form.discount_percentage);
    if (isNaN(disc) || disc <= 0 || disc > 100) {
      toast.error('Discount must be between 1 and 100'); return false;
    }
    if (!form.email || !/\S+@\S+\.\S+/.test(form.email)) {
      toast.error('Valid email is required'); return false;
    }
    if (!form.password || form.password.length < 6) {
      toast.error('Password must be at least 6 characters'); return false;
    }
    return true;
  };

  const handleNext = () => {
    if (validateStep1()) setStep(2);
  };

  const handleSubmit = async () => {
    if (!validateStep2()) return;
    setLoading(true);
    try {
      const payload = {
        ...form,
        coupon_code: form.coupon_code.toUpperCase().trim(),
        discount_percentage: parseFloat(form.discount_percentage),
        payout_cycle_days: parseInt(form.payout_cycle_days),
        phone: form.phone.replace(/\D/g, ''),
      };
      const res = await axios.post(`${API}/superadmin/influencers`, payload, authHeaders);
      setCreated({ ...res.data, ...form });
      toast.success('Growth Partner registered successfully!');
      onCreated && onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create influencer');
    } finally {
      setLoading(false);
    }
  };

  const buildWhatsAppMessage = () => {
    if (!created) return '';
    const name = `${form.first_name} ${form.last_name}`;
    const msg = `Hello ${name},\n\nGreetings from X7 Autoz! 🚗\n\nWe have successfully registered you as a Growth Partner for ${form.city} city.\n\nYour Unique ID: *${created.influencer_uid}*\nPlease save this unique ID for future reference.\n\nYour Coupon Code: *${form.coupon_code.toUpperCase()}*\nDiscount Percentage: *${form.discount_percentage}%*\n\nHere are your Login Details:\nEmail: ${form.email}\nPassword: ${form.password}\n\nTo login, paste this link in any browser:\nwww.x7autoz.com/login\n\nWelcome to the X7 Autoz family!\n\nBest regards,\nX7 Autoz Team`;
    return encodeURIComponent(msg);
  };

  const openWhatsApp = () => {
    const phone = form.phone.replace(/\D/g, '');
    const intlPhone = phone.startsWith('91') ? phone : `91${phone}`;
    window.open(`https://wa.me/${intlPhone}?text=${buildWhatsAppMessage()}`, '_blank');
  };

  const handleClose = () => {
    setStep(1);
    setForm(defaultForm);
    setCreated(null);
    setCitySearch('');
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-5 w-5 text-purple-600" />
            {created ? 'Growth Partner Registered!' : `Create Growth Partner — Step ${step} of 2`}
          </DialogTitle>
        </DialogHeader>

        {/* Step indicator */}
        {!created && (
          <div className="flex items-center gap-2 mb-4">
            {[1, 2].map((s) => (
              <div key={s} className="flex items-center gap-2">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold ${step >= s ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-400'}`}>
                  {step > s ? <CheckCircle className="h-4 w-4" /> : s}
                </div>
                <span className={`text-xs ${step === s ? 'text-purple-700 font-medium' : 'text-gray-400'}`}>
                  {s === 1 ? 'Basic Info' : 'Coupon & Access'}
                </span>
                {s < 2 && <ChevronRight className="h-3 w-3 text-gray-300" />}
              </div>
            ))}
          </div>
        )}

        {/* ── SUCCESS SCREEN ── */}
        {created && (
          <div className="space-y-5">
            <div className="text-center p-5 bg-green-50 rounded-xl border border-green-200">
              <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
              <h3 className="font-semibold text-green-800 text-lg">Registration Complete</h3>
              <p className="text-green-600 text-sm mt-1">Growth Partner ID: <strong className="font-mono">{created.influencer_uid}</strong></p>
            </div>

            {/* Details recap */}
            <div className="bg-gray-50 rounded-xl p-4 space-y-2 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div><span className="text-gray-500">Name:</span> <strong>{form.first_name} {form.last_name}</strong></div>
                <div><span className="text-gray-500">City:</span> <strong>{form.city}</strong></div>
                <div><span className="text-gray-500">Phone:</span> <strong>{form.phone}</strong></div>
                <div><span className="text-gray-500">Email:</span> <strong>{form.email}</strong></div>
                <div><span className="text-gray-500">Coupon:</span> <strong className="font-mono text-purple-700">{form.coupon_code.toUpperCase()}</strong></div>
                <div><span className="text-gray-500">Discount:</span> <strong>{form.discount_percentage}%</strong></div>
                <div><span className="text-gray-500">Payout:</span> <strong>Every {form.payout_cycle_days} days</strong></div>
                <div><span className="text-gray-500">Vehicles:</span> <strong>{form.vehicle_types.join(', ')}</strong></div>
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={openWhatsApp}
                className="flex-1 bg-green-500 hover:bg-green-600 text-white"
              >
                <MessageCircle className="h-4 w-4 mr-2" />
                Send on WhatsApp
              </Button>
              <Button variant="outline" className="flex-1" onClick={handleClose}>
                Close
              </Button>
            </div>
          </div>
        )}

        {/* ── STEP 1: BASIC INFO ── */}
        {!created && step === 1 && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>First Name <span className="text-red-500">*</span></Label>
                <Input
                  value={form.first_name}
                  onChange={e => set('first_name', e.target.value)}
                  placeholder="e.g. Arjun"
                />
              </div>
              <div>
                <Label>Last Name <span className="text-red-500">*</span></Label>
                <Input
                  value={form.last_name}
                  onChange={e => set('last_name', e.target.value)}
                  placeholder="e.g. Sharma"
                />
              </div>
            </div>

            <div>
              <Label>Phone Number <span className="text-red-500">*</span></Label>
              <div className="flex">
                <div className="flex items-center px-3 bg-gray-100 rounded-l-md text-gray-500 text-sm border border-r-0 border-gray-200">+91</div>
                <Input
                  className="rounded-l-none"
                  value={form.phone}
                  onChange={e => set('phone', e.target.value.replace(/\D/g, '').slice(0, 10))}
                  placeholder="9876543210"
                />
              </div>
            </div>

            <div>
              <Label>Email Address <span className="text-red-500">*</span></Label>
              <Input
                type="email"
                value={form.email}
                onChange={e => set('email', e.target.value.toLowerCase())}
                placeholder="partner@email.com"
              />
            </div>

            <div>
              <Label>City <span className="text-red-500">*</span></Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 z-10" />
                <Select
                  value={form.city}
                  onValueChange={v => { set('city', v); setCitySearch(''); }}
                >
                  <SelectTrigger className="pl-9">
                    <SelectValue placeholder="Search & select city" />
                  </SelectTrigger>
                  <SelectContent>
                    <div className="px-2 py-1">
                      <Input
                        value={citySearch}
                        onChange={e => setCitySearch(e.target.value)}
                        placeholder="Search city..."
                        className="h-8 text-sm"
                        onClick={e => e.stopPropagation()}
                      />
                    </div>
                    {filteredCities.map(c => (
                      <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                    ))}
                    {!filteredCities.length && (
                      <div className="px-4 py-2 text-sm text-gray-400">No cities found</div>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <Button variant="outline" className="flex-1" onClick={handleClose}>Cancel</Button>
              <Button className="flex-1 bg-purple-600 hover:bg-purple-700 text-white" onClick={handleNext}>
                Next <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* ── STEP 2: COUPON & ACCESS ── */}
        {!created && step === 2 && (
          <div className="space-y-4">
            {/* Step 1 summary */}
            <div className="bg-purple-50 border border-purple-100 rounded-xl p-3 text-sm">
              <p className="text-purple-800 font-medium mb-1">Step 1 Summary</p>
              <div className="grid grid-cols-2 gap-1 text-purple-700">
                <span>Name: <strong>{form.first_name} {form.last_name}</strong></span>
                <span>City: <strong>{form.city}</strong></span>
                <span>Phone: <strong>{form.phone}</strong></span>
                <span>Email: <strong className="text-xs">{form.email}</strong></span>
              </div>
            </div>

            {/* Coupon Code */}
            <div>
              <Label className="flex items-center gap-1">
                <Tag className="h-3.5 w-3.5 text-purple-600" />
                Coupon Code <span className="text-red-500">*</span>
                <span className="text-gray-400 text-xs ml-1">(6–8 chars, auto-uppercase)</span>
              </Label>
              <Input
                className="font-mono uppercase tracking-widest"
                value={form.coupon_code}
                maxLength={8}
                onChange={e => set('coupon_code', e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                placeholder="e.g. ARJUN30"
              />
              {form.coupon_code && (
                <p className={`text-xs mt-1 ${form.coupon_code.length >= 6 ? 'text-green-600' : 'text-orange-500'}`}>
                  {form.coupon_code.length}/8 characters {form.coupon_code.length >= 6 ? '✓ valid' : '(min 6)'}
                </p>
              )}
            </div>

            {/* Vehicle Types */}
            <div>
              <Label className="flex items-center gap-1 mb-2">
                Vehicle Types for Discount <span className="text-red-500">*</span>
                <span className="text-gray-400 text-xs">(multi-select)</span>
              </Label>
              <div className="grid grid-cols-2 gap-2">
                {VEHICLE_TYPE_OPTIONS.map(v => (
                  <button
                    key={v.key}
                    type="button"
                    onClick={() => toggleVehicleType(v.key)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-medium transition-all ${
                      form.vehicle_types.includes(v.key)
                        ? 'border-purple-500 bg-purple-50 text-purple-700'
                        : 'border-gray-200 bg-gray-50 text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <span>{v.emoji}</span>
                    {v.label}
                    {form.vehicle_types.includes(v.key) && (
                      <CheckCircle className="h-3.5 w-3.5 ml-auto text-purple-600" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Discount % */}
            <div>
              <Label className="flex items-center gap-1">
                <Percent className="h-3.5 w-3.5 text-purple-600" />
                Discount on Base Price (%) <span className="text-red-500">*</span>
              </Label>
              <Input
                type="number"
                min="1"
                max="100"
                value={form.discount_percentage}
                onChange={e => set('discount_percentage', e.target.value)}
                placeholder="e.g. 30"
              />
              {form.discount_percentage && (
                <p className="text-xs text-gray-500 mt-1">
                  Influencer earns 10% of net discount (after GST exclusion) per transaction.
                </p>
              )}
            </div>

            {/* Payout Cycle */}
            <div>
              <Label className="flex items-center gap-1">
                <Shield className="h-3.5 w-3.5 text-purple-600" />
                Payout Cycle
              </Label>
              <div className="flex gap-3">
                {[15, 30].map(days => (
                  <button
                    key={days}
                    type="button"
                    onClick={() => set('payout_cycle_days', days)}
                    className={`flex-1 py-2.5 rounded-xl border text-sm font-medium transition-all ${
                      form.payout_cycle_days === days
                        ? 'border-purple-500 bg-purple-50 text-purple-700'
                        : 'border-gray-200 bg-gray-50 text-gray-600'
                    }`}
                  >
                    Every {days} days
                  </button>
                ))}
              </div>
            </div>

            {/* Login Credentials */}
            <div className="border-t pt-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Login Credentials</p>
              <div className="space-y-3">
                <div>
                  <Label>Login Email (Username)</Label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={e => set('email', e.target.value.toLowerCase())}
                    placeholder="login email"
                  />
                </div>
                <div>
                  <Label>Password <span className="text-red-500">*</span></Label>
                  <div className="relative">
                    <Input
                      type={showPass ? 'text' : 'password'}
                      value={form.password}
                      onChange={e => set('password', e.target.value)}
                      placeholder="Min 6 characters"
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPass(!showPass)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                    >
                      {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setStep(1)}>
                <ChevronLeft className="h-4 w-4 mr-1" /> Back
              </Button>
              <Button
                className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Register Growth Partner
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}